import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { Children, cloneElement, isValidElement, type ComponentProps } from 'react';
import { useAccordionIds } from '~/hooks/unique-id';

export const DetailAccordion = ({ children, ...props }: ComponentProps<typeof Accordion>) => {
  const ids = useAccordionIds();
  // MUI names the panel's region from the summary's own props, so the ids go on that element.
  const [summary, ...details] = Children.toArray(children);
  return (
    <Accordion disableGutters {...props}>
      {isValidElement(summary) ? cloneElement(summary, ids) : summary}
      {details}
    </Accordion>
  );
};

export const DetailAccordionContainer = ({ children, ...props }: ComponentProps<typeof Box>) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }} {...props}>
    {children}
  </Box>
);

export const DetailAccordionDetails = ({
  children,
  ...props
}: ComponentProps<typeof AccordionDetails>) => (
  <AccordionDetails
    sx={{
      '& > *': { width: '100%' },
      alignItems: 'flex-start',
      display: 'flex',
      flexDirection: 'column',
      gap: 1,
      padding: (theme) => theme.spacing(2, 0, 0, 0),
    }}
    {...props}
  >
    {children}
  </AccordionDetails>
);

export const DetailAccordionSummary = ({
  children,
  ...props
}: ComponentProps<typeof AccordionSummary>) => (
  <AccordionSummary
    expandIcon={<ExpandMore />}
    sx={{
      minHeight: '40px',
      '&.Mui-expanded': { minHeight: '40px' },
      '.MuiAccordionSummary-content': {
        fontSize: 12,
        fontWeight: 600,
        margin: 0,
        textTransform: 'uppercase',
      },
    }}
    {...props}
  >
    {children}
  </AccordionSummary>
);
